// src/components/Auth.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // <-- 1. IMPORTA GLI HOOK
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from '../firebaseConfig';
import { useTranslation } from 'react-i18next';
import { logAudit, AUDIT_ACTIONS } from '../utils/auditLog';

// --- IMPORT MUI COMPONENTS ---
import { 
  Button, 
  TextField, 
  Box, 
  Typography,
  Link,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';

// --- FUNZIONE HELPER PER TRADURRE GLI ERRORI DI FIREBASE ---
const getFirebaseErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'errors.invalid_credential';
    case 'auth/email-already-in-use':
      return 'errors.email_already_in_use';
    case 'auth/weak-password':
      return 'errors.weak_password';
    case 'auth/invalid-email':
      return 'errors.invalid_email';
    default:
      return 'errors.unknown';
  }
};


function Auth() {
  const { t } = useTranslation('auth');
  
  // --- 2. INIZIALIZZA GLI HOOK ---
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsLoading(true);
    setError('');

    if (isRegistering) {
      // Registrazione
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, "users", user.uid), {
          email: user.email,
          uid: user.uid,
          teamId: null, 
          subscriptionPlan: "Free" 
        });
        
        console.log("User registered:", user.uid);

        // --- 3. PULIZIA URL (Registrazione) ---
        // Rimuove eventuali parametri (come invitationId) dall'URL
        // prima che l'App reindirizzi, evitando loop di errore.
        navigate(location.pathname, { replace: true });

      } catch (err) {
        console.error("Errore registrazione:", err.code);
        setError(getFirebaseErrorMessage(err.code));
      }
    } else {
      // Login
      try {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        logAudit(cred.user.uid, null, AUDIT_ACTIONS.LOGIN, { email });
        console.log("User logged in!");

        // --- 3. PULIZIA URL (Login) ---
        // Rimuove eventuali parametri vecchi
        navigate(location.pathname, { replace: true });

      } catch (err) {
        console.error("Errore accesso:", err.code);
        setError(getFirebaseErrorMessage(err.code));
      }
    }
    
    setIsLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError(t('forgot_password_empty'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, email);
      alert(t('forgot_password_success'));
    } catch (err) {
      console.error(err);
      setError(t('forgot_password_error'));
    }
    setIsLoading(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3
      }}
    >
      <Paper 
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          borderRadius: 3,
          width: '100%',
          maxWidth: '440px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/MSlogo.png"
          alt="MotorsportSuite Logo"
          sx={{
            height: 100,
            mb: 2,
            filter: (theme) => (theme.palette.mode === 'light' ? 'invert(1)' : 'none')
          }}
        />
        
        <Tabs 
          value={isRegistering ? 1 : 0} 
          onChange={(e, newValue) => {
            if (isLoading) return;
            setError('');
            setIsRegistering(newValue === 1);
          }}
          variant="fullWidth"
          sx={{ width: '100%', mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('tab_login')} sx={{ fontWeight: 'bold' }} />
          <Tab label={t('tab_signup')} sx={{ fontWeight: 'bold' }} />
        </Tabs>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2, mb: 1 }}>
            {t(error)} 
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('email_label')} 
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t('password_label')}
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading} 
            sx={{ mt: 3, mb: 2, height: '56px' }}
          >
            {isLoading ? 
              <CircularProgress size={24} color="inherit" /> : 
              (isRegistering ? t('signup_button') : t('login_button'))
            }
          </Button>
          
          {!isRegistering && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Link 
                href="#" 
                variant="body2" 
                onClick={handleResetPassword}
              >
                {t('forgot_password')}
              </Link>
            </Box>
          )}
          
        </Box>
      </Paper>
    </Box>
  );
}

export default Auth;